import {
  Accordion,
  Alert,
  Anchor,
  Badge,
  Card,
  Code,
  List,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  rem,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import {
  IconAlertCircle,
  IconBrackets,
  IconCode,
  IconDatabase,
  IconEye,
  IconInfoCircle,
  IconKey,
  IconList,
  IconLock,
  IconPencil,
  IconSettings,
  IconTags,
} from "@tabler/icons-react";
import { PageHeader } from "../../components/PageHeader";

const useStyles = createStyles((theme) => ({
  section: {
    marginBottom: theme.spacing.xl,
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  },
  card: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
  },
  codeBlock: {
    backgroundColor: theme.colors.dark[8],
    border: `1px solid ${theme.colors.dark[5]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
    fontFamily: "monospace",
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.6,
    overflowX: "auto" as const,
    whiteSpace: "pre" as const,
    color: theme.colors.dark[0],
  },
  statusTable: {
    "& td, & th": {
      padding: `${rem(8)} ${rem(12)}`,
    },
  },
}));

const GqlBlock = ({ children }: { children: string }) => {
  const { classes } = useStyles();
  return <div className={classes.codeBlock}>{children.trim()}</div>;
};

const SectionHeader = ({
  icon,
  title,
  id,
}: {
  icon: React.ReactNode;
  title: string;
  id: string;
}) => {
  const { classes } = useStyles();
  return (
    <div className={classes.sectionTitle} id={id}>
      <ThemeIcon size="md" radius="md" variant="light" color="cyan">
        {icon}
      </ThemeIcon>
      <Title order={2}>{title}</Title>
    </div>
  );
};

export const DataDocumentation = () => {
  const { classes } = useStyles();

  return (
    <div className="wp-fade-in">
      <PageHeader
        title="Data Documentation"
        subtitle="Complete reference for Custom Fields and Object Metadata"
        showBreadcrumbs={false}
      />

      {/* Table of Contents */}
      <Card className={classes.card} p="md" radius="md" mb="xl">
        <Text fw={600} mb="sm">
          Table of Contents
        </Text>
        <SimpleGrid cols={{ base: 1, md: 3 }}>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#overview">Overview</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#custom-field-definitions">
                Custom Field Definitions
              </Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#field-types">Field Types</Anchor>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#custom-field-values">Custom Field Values</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#object-metadata">Object Metadata</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#supported-objects">Supported Object Types</Anchor>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#visibility-editability">
                Visibility & Editability
              </Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#graphql">GraphQL API Reference</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#data-model">Data Model</Anchor>
            </List.Item>
          </List>
        </SimpleGrid>
      </Card>

      <Stack gap="xl">
        {/* ===================== OVERVIEW ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconDatabase size={18} />}
            title="Overview"
            id="overview"
          />
          <Text mb="md">
            The Data service provides two complementary systems for extending
            entities across the WellPlayed platform with additional data:
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} mb="md">
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs" c="cyan">
                Custom Fields
              </Text>
              <Text size="sm">
                Structured, typed fields defined per object type. You create
                field definitions (schema) once, and then set values for those
                fields on individual objects. Fields support validation,
                required constraints, uniqueness, visibility controls, and
                editability rules.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs" c="teal">
                Object Metadata
              </Text>
              <Text size="sm">
                A flexible key-value store for attaching arbitrary data to any
                supported object. Unlike Custom Fields, metadata does not
                require a definition — you can add any key-value pair on the
                fly. Each entry has a type (String, Boolean, URL, Image URL) and
                a visibility setting (Public or Private).
              </Text>
            </Card>
          </SimpleGrid>

          <Alert icon={<IconInfoCircle />} color="indigo" variant="light">
            Both systems can be managed through the console UI (embedded panels
            in entity detail screens) or programmatically via the GraphQL API.
            Custom Field definitions are managed centrally in{" "}
            <Text fw={700} span>
              Data &gt; Custom Fields
            </Text>
            .
          </Alert>
        </div>

        {/* ===================== CUSTOM FIELD DEFINITIONS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconSettings size={18} />}
            title="Custom Field Definitions"
            id="custom-field-definitions"
          />
          <Text mb="md">
            Custom Field Definitions are the schema for your custom fields. Each
            definition specifies:
          </Text>
          <List spacing="xs" size="sm" mb="md">
            <List.Item>
              <Text fw={600} span>
                Object Type
              </Text>{" "}
              — which entity type this field applies to (e.g. Tournament,
              Player, Event)
            </List.Item>
            <List.Item>
              <Text fw={600} span>
                Key
              </Text>{" "}
              — a unique identifier used in the API (alphanumeric, hyphens,
              underscores, 1-100 chars)
            </List.Item>
            <List.Item>
              <Text fw={600} span>
                Name
              </Text>{" "}
              — a human-readable display name
            </List.Item>
            <List.Item>
              <Text fw={600} span>
                Type
              </Text>{" "}
              — the data type (String, Number, Boolean, Date, Email, Phone, URL,
              Country, Select, Multi Select)
            </List.Item>
            <List.Item>
              <Text fw={600} span>
                Required / Unique
              </Text>{" "}
              — validation constraints
            </List.Item>
            <List.Item>
              <Text fw={600} span>
                Visibility
              </Text>{" "}
              — who can read the field value
            </List.Item>
            <List.Item>
              <Text fw={600} span>
                Editability
              </Text>{" "}
              — who can write the field value
            </List.Item>
            <List.Item>
              <Text fw={600} span>
                Options
              </Text>{" "}
              — for Select and Multi Select types, a list of label/value pairs
            </List.Item>
            <List.Item>
              <Text fw={600} span>
                Default Value
              </Text>{" "}
              — optional default when no value is set
            </List.Item>
            <List.Item>
              <Text fw={600} span>
                Validation Regex
              </Text>{" "}
              — optional regex pattern for additional validation
            </List.Item>
          </List>

          <Title order={4} mb="xs">
            Creating a Definition
          </Title>
          <Text mb="sm">
            Navigate to{" "}
            <Text fw={700} span>
              Data &gt; Custom Fields &gt; Create
            </Text>{" "}
            or use the GraphQL mutation:
          </Text>
          <GqlBlock>
            {`mutation ($input: CreateCustomFieldDefinitionInput!) {
  createCustomFieldDefinition(input: $input) {
    id
    objectType
    key
    name
    type
    required
    unique
    visibility
    editability
    order
  }
}

# CreateCustomFieldDefinitionInput:
# {
#   objectType: String!       # e.g. "Tournament", "Player", "Event"
#   key: String!              # Unique identifier (alphanumeric + hyphens/underscores)
#   name: String!             # Display name
#   description: String       # Optional help text
#   type: CustomFieldType!    # STRING, NUMBER, BOOLEAN, DATE, EMAIL, PHONE, URL, COUNTRY, SELECT, MULTI_SELECT
#   required: Boolean         # Default: false
#   unique: Boolean           # Default: false
#   visibility: PropertyVisibility!   # PUBLIC, OWNER, WITH_PERMISSION, OWNER_OR_PERMISSION
#   editability: PropertyEditability! # ALWAYS, ONE_TIME, WITH_PERMISSION
#   options: [CustomFieldOptionInput!]  # For SELECT/MULTI_SELECT: [{ label, value }]
#   defaultValue: String      # Optional default
#   validationRegex: String   # Optional regex pattern
# }`}
          </GqlBlock>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mt="md"
          >
            The number of custom field definitions you can create is limited by
            your subscription&apos;s <Code>CUSTOM_FIELDS</Code> quota, and that
            quota is counted{" "}
            <Text fw={600} span>
              independently for each object type
            </Text>
            . For example, a quota of 2 lets you create up to 2 fields on
            Tournament <Text span>and</Text> up to 2 fields on Event (and so on
            for every entity type) — it is not a shared total across all
            entities. Creating a field beyond the quota for an object type
            returns <Code>LIMIT_REACHED</Code>.
          </Alert>

          <Title order={4} mt="lg" mb="xs">
            Ordering Definitions
          </Title>
          <Text mb="sm">
            Definitions can be reordered per object type using drag-and-drop in
            the console, or via the GraphQL mutation:
          </Text>
          <GqlBlock>
            {`mutation ($objectType: String!, $orderedIds: [ID!]!) {
  reorderCustomFieldDefinitions(
    objectType: $objectType
    orderedIds: $orderedIds
  )
}`}
          </GqlBlock>
        </div>

        {/* ===================== FIELD TYPES ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconBrackets size={18} />}
            title="Field Types"
            id="field-types"
          />
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Type</th>
                <th>Description</th>
                <th>Stored As</th>
                <th>Validation</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Badge variant="light">STRING</Badge>
                </td>
                <td>Free-form text</td>
                <td>String</td>
                <td>Optional regex</td>
              </tr>
              <tr>
                <td>
                  <Badge variant="light">NUMBER</Badge>
                </td>
                <td>Numeric value</td>
                <td>String (parsed as number)</td>
                <td>Must be a valid number</td>
              </tr>
              <tr>
                <td>
                  <Badge variant="light">BOOLEAN</Badge>
                </td>
                <td>True/false toggle</td>
                <td>
                  <Code>"true"</Code> or <Code>"false"</Code>
                </td>
                <td>-</td>
              </tr>
              <tr>
                <td>
                  <Badge variant="light">DATE</Badge>
                </td>
                <td>Date and time</td>
                <td>ISO 8601 string</td>
                <td>Must be valid date</td>
              </tr>
              <tr>
                <td>
                  <Badge variant="light">EMAIL</Badge>
                </td>
                <td>Email address</td>
                <td>String</td>
                <td>Email format validation</td>
              </tr>
              <tr>
                <td>
                  <Badge variant="light">PHONE</Badge>
                </td>
                <td>Phone number</td>
                <td>String</td>
                <td>Phone format validation</td>
              </tr>
              <tr>
                <td>
                  <Badge variant="light">URL</Badge>
                </td>
                <td>Web URL</td>
                <td>String</td>
                <td>
                  Must be valid URL with <Code>http://</Code> or{" "}
                  <Code>https://</Code>
                </td>
              </tr>
              <tr>
                <td>
                  <Badge variant="light">COUNTRY</Badge>
                </td>
                <td>Country code</td>
                <td>String</td>
                <td>
                  ISO 3166-1 alpha-2 code (e.g. <Code>US</Code>, <Code>FR</Code>
                  )
                </td>
              </tr>
              <tr>
                <td>
                  <Badge variant="light">SELECT</Badge>
                </td>
                <td>Single choice from options</td>
                <td>String (selected value)</td>
                <td>Must match a defined option</td>
              </tr>
              <tr>
                <td>
                  <Badge variant="light">MULTI_SELECT</Badge>
                </td>
                <td>Multiple choices from options</td>
                <td>Comma-separated string</td>
                <td>Each value must match a defined option</td>
              </tr>
            </tbody>
          </Table>

          <Alert icon={<IconInfoCircle />} color="indigo" variant="light">
            All values are stored as strings internally. The type determines
            which input component is rendered in the console and which
            validation rules are applied.
          </Alert>
        </div>

        {/* ===================== CUSTOM FIELD VALUES ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconPencil size={18} />}
            title="Custom Field Values"
            id="custom-field-values"
          />
          <Text mb="md">
            Once definitions are created, values can be set on individual
            objects. Values are managed through the embedded Custom Fields panel
            in entity detail screens, or via the GraphQL API.
          </Text>

          <Title order={4} mb="xs">
            Querying Values
          </Title>
          <GqlBlock>
            {`query ($objectType: String!, $objectId: ID!) {
  customFieldValues(objectType: $objectType, objectId: $objectId) {
    definition {
      id
      key
      name
      type
      required
      unique
      visibility
      editability
      options { label, value }
      defaultValue
      validationRegex
    }
    value
  }
}`}
          </GqlBlock>

          <Title order={4} mt="lg" mb="xs">
            Setting Values
          </Title>
          <Text mb="sm">
            Use the <Code>setCustomFieldValues</Code> mutation to set one or
            more field values on an object:
          </Text>
          <GqlBlock>
            {`mutation ($input: SetCustomFieldValuesInput!) {
  setCustomFieldValues(input: $input) {
    definition {
      id
      key
      name
      type
    }
    value
  }
}

# SetCustomFieldValuesInput:
# {
#   objectType: String!     # e.g. "Tournament"
#   objectId: ID!           # The entity's ID
#   fields: [CustomFieldValueInput!]!
#   deleteKeys: [String!]   # Optional: keys whose stored values should be deleted
# }
#
# CustomFieldValueInput:
# {
#   key: String!            # Matches the definition key
#   value: String!          # The value to set
# }`}
          </GqlBlock>

          <Title order={4} mt="lg" mb="xs">
            Deleting stored values{" "}
            <Badge color="teal" variant="light" size="sm" ml="xs">
              New
            </Badge>
          </Title>
          <Text mb="sm">
            Pass <Code>deleteKeys</Code> alongside (or instead of){" "}
            <Code>fields</Code> to atomically remove stored values for the
            listed keys. Upserts in <Code>fields</Code> and deletions in{" "}
            <Code>deleteKeys</Code> are applied in the same database
            transaction.
          </Text>
          <GqlBlock>
            {`mutation DeleteCustomFieldValues($input: SetCustomFieldValuesInput!) {
  setCustomFieldValues(input: $input) {
    definition { key, name, type }
    value   # null for keys that were just deleted
  }
}

# Example: delete two stored values, update one
# {
#   objectType: "Tournament"
#   objectId: "abc123"
#   fields: [{ key: "region", value: "EU" }]
#   deleteKeys: ["legacy-tag", "scratch-notes"]
# }`}
          </GqlBlock>

          <Alert
            icon={<IconAlertCircle />}
            color="orange"
            variant="light"
            mt="md"
          >
            <List size="sm" spacing={4}>
              <List.Item>
                <strong>Maximum 100 keys per call.</strong>{" "}
                <Code>deleteKeys</Code> is capped at 100 entries per mutation
                invocation.
              </List.Item>
              <List.Item>
                <strong>Required fields.</strong> Passing a required field's
                key in <Code>deleteKeys</Code> returns{" "}
                <Code>CUSTOM_FIELD_REQUIRED_VALUE_NOT_DELETABLE</Code> unless
                the caller holds{" "}
                <Code>organization:custom_fields:values:manage</Code>. A plain
                owner without that permission cannot delete a required value.
              </List.Item>
              <List.Item>
                <strong>No overlap allowed.</strong> A key must not appear in
                both <Code>fields</Code> and <Code>deleteKeys</Code> — this
                returns <Code>CUSTOM_FIELD_KEY_IN_BOTH_SET_AND_DELETE</Code>.
              </List.Item>
              <List.Item>
                <strong>ONE_TIME editability.</strong> Only callers holding
                the <Code>organization:custom_fields:values:manage</Code>{" "}
                permission can delete a value stored under a{" "}
                <Code>ONE_TIME</Code> field; the object owner cannot.
              </List.Item>
            </List>
          </Alert>

          <Alert
            icon={<IconAlertCircle />}
            color="orange"
            variant="light"
            mt="md"
          >
            Validation is enforced server-side: required fields must have a
            value, unique fields are checked for duplicates, regex patterns are
            applied, and type-specific validations (email format, URL format,
            etc.) are run before saving.
          </Alert>
        </div>

        {/* ===================== OBJECT METADATA ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconTags size={18} />}
            title="Object Metadata"
            id="object-metadata"
          />
          <Text mb="md">
            Object Metadata is a flexible key-value store that does not require
            predefined definitions. You can add any key-value pair to any
            supported object at any time.
          </Text>

          <SimpleGrid cols={{ base: 1, sm: 2 }} mb="md">
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                Metadata Entry Properties
              </Text>
              <List size="sm" spacing={4}>
                <List.Item>
                  <Code>key</Code> — Unique identifier (alphanumeric +
                  hyphens/underscores, 1-100 chars)
                </List.Item>
                <List.Item>
                  <Code>value</Code> — The stored value (up to 4096 chars)
                </List.Item>
                <List.Item>
                  <Code>type</Code> — STRING, BOOLEAN, IMAGE_URL, or URL
                </List.Item>
                <List.Item>
                  <Code>visibility</Code> — PUBLIC or PRIVATE
                </List.Item>
              </List>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                Use Cases
              </Text>
              <List size="sm" spacing={4}>
                <List.Item>Store external system IDs or references</List.Item>
                <List.Item>
                  Attach image URLs (logos, banners, avatars)
                </List.Item>
                <List.Item>Add feature flags per entity</List.Item>
                <List.Item>
                  Store configuration that doesn&apos;t fit custom fields
                </List.Item>
                <List.Item>
                  Private data visible only to admins (PRIVATE visibility)
                </List.Item>
              </List>
            </Card>
          </SimpleGrid>

          <Title order={4} mb="xs">
            Querying Metadata
          </Title>
          <GqlBlock>
            {`query ($objectType: String!, $objectId: ID!) {
  objectMetadata(objectType: $objectType, objectId: $objectId) {
    key
    value
    type
    visibility
    objectType
    objectId
    createdAt
    updatedAt
  }
}`}
          </GqlBlock>

          <Title order={4} mt="lg" mb="xs">
            Setting Metadata
          </Title>
          <Text mb="sm">
            Use the <Code>setObjectMetadata</Code> mutation to add or update
            metadata entries. Entries are upserted by key — if the key already
            exists, the value is updated.
          </Text>
          <GqlBlock>
            {`mutation ($input: SetObjectMetadataInput!) {
  setObjectMetadata(input: $input) {
    key
    value
    type
    visibility
    createdAt
    updatedAt
  }
}

# SetObjectMetadataInput:
# {
#   objectType: String!
#   objectId: ID!
#   entries: [ObjectMetadataEntryInput!]!
# }
#
# ObjectMetadataEntryInput:
# {
#   key: String!                    # 1-100 chars, alphanumeric + hyphens/underscores
#   value: String!                  # Up to 4096 chars
#   type: MetadataType!             # STRING, BOOLEAN, IMAGE_URL, URL
#   visibility: MetadataVisibility! # PUBLIC, PRIVATE
# }`}
          </GqlBlock>

          <Title order={4} mt="lg" mb="xs">
            Removing Metadata
          </Title>
          <GqlBlock>
            {`mutation ($input: RemoveObjectMetadataInput!) {
  removeObjectMetadata(input: $input)
}

# RemoveObjectMetadataInput:
# {
#   objectType: String!
#   objectId: ID!
#   keys: [String!]!    # Keys to remove
# }`}
          </GqlBlock>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mt="md"
          >
            URL and Image URL types are validated to ensure they use{" "}
            <Code>http://</Code> or <Code>https://</Code> protocols. Key
            uniqueness is enforced per object — you cannot have duplicate keys
            on the same object.
          </Alert>
        </div>

        {/* ===================== SUPPORTED OBJECT TYPES ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconList size={18} />}
            title="Supported Object Types"
            id="supported-objects"
          />
          <Text mb="md">
            Custom Fields and Object Metadata are supported on the following
            entity types. In the console, embedded panels are available in each
            entity&apos;s detail/settings screens.
          </Text>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Object Type</th>
                <th>Service</th>
                <th>Console Location</th>
                <th>Custom Fields</th>
                <th>Metadata</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>Tournament</Code>
                </td>
                <td>Tournaments</td>
                <td>Tournament &gt; Settings</td>
                <td>
                  <Badge color="green" size="xs" variant="light">
                    Yes
                  </Badge>
                </td>
                <td>
                  <Badge color="green" size="xs" variant="light">
                    Yes
                  </Badge>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>TournamentStep</Code>
                </td>
                <td>Tournaments</td>
                <td>Tournament &gt; Step detail</td>
                <td>
                  <Badge color="green" size="xs" variant="light">
                    Yes
                  </Badge>
                </td>
                <td>
                  <Badge color="green" size="xs" variant="light">
                    Yes
                  </Badge>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>TournamentTeam</Code>
                </td>
                <td>Tournaments</td>
                <td>Tournament &gt; Teams</td>
                <td>
                  <Badge color="green" size="xs" variant="light">
                    Yes
                  </Badge>
                </td>
                <td>
                  <Badge color="green" size="xs" variant="light">
                    Yes
                  </Badge>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>Event</Code>
                </td>
                <td>Events</td>
                <td>Event &gt; Settings</td>
                <td>
                  <Badge color="green" size="xs" variant="light">
                    Yes
                  </Badge>
                </td>
                <td>
                  <Badge color="green" size="xs" variant="light">
                    Yes
                  </Badge>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>EventVenue</Code>
                </td>
                <td>Events</td>
                <td>Event &gt; Venues &gt; Venue detail</td>
                <td>
                  <Badge color="green" size="xs" variant="light">
                    Yes
                  </Badge>
                </td>
                <td>
                  <Badge color="green" size="xs" variant="light">
                    Yes
                  </Badge>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>Leaderboard</Code>
                </td>
                <td>Leaderboards</td>
                <td>Leaderboard detail</td>
                <td>
                  <Badge color="green" size="xs" variant="light">
                    Yes
                  </Badge>
                </td>
                <td>
                  <Badge color="green" size="xs" variant="light">
                    Yes
                  </Badge>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>Platform</Code>
                </td>
                <td>Platform</td>
                <td>Platform detail</td>
                <td>
                  <Badge color="green" size="xs" variant="light">
                    Yes
                  </Badge>
                </td>
                <td>
                  <Badge color="green" size="xs" variant="light">
                    Yes
                  </Badge>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>PlatformCdnFile</Code>
                </td>
                <td>Platform</td>
                <td>Platform &gt; Files &gt; File detail</td>
                <td>
                  <Badge color="green" size="xs" variant="light">
                    Yes
                  </Badge>
                </td>
                <td>
                  <Badge color="green" size="xs" variant="light">
                    Yes
                  </Badge>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>Webhook</Code>
                </td>
                <td>Developer</td>
                <td>Developer &gt; Webhooks &gt; Webhook detail</td>
                <td>
                  <Badge color="green" size="xs" variant="light">
                    Yes
                  </Badge>
                </td>
                <td>
                  <Badge color="green" size="xs" variant="light">
                    Yes
                  </Badge>
                </td>
              </tr>
              <tr>
                <td>
                  <Code>Shop</Code>
                </td>
                <td>Gamification</td>
                <td>Gamification &gt; Shops &gt; Shop detail</td>
                <td>
                  <Badge color="green" size="xs" variant="light">
                    Yes
                  </Badge>
                </td>
                <td>
                  <Badge color="green" size="xs" variant="light">
                    Yes
                  </Badge>
                </td>
              </tr>
            </tbody>
          </Table>

          <Alert icon={<IconInfoCircle />} color="indigo" variant="light">
            The list of supported object types is dynamic and may expand over
            time. Use the <Code>allowedObjectTypes</Code> query to get the
            current list programmatically.
          </Alert>
        </div>

        {/* ===================== VISIBILITY & EDITABILITY ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconEye size={18} />}
            title="Visibility & Editability"
            id="visibility-editability"
          />

          <Title order={4} mb="xs">
            Custom Field Visibility
          </Title>
          <Text mb="sm">Controls who can read the field value:</Text>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="lg"
          >
            <thead>
              <tr>
                <th>Visibility</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Badge variant="light" color="green">
                    PUBLIC
                  </Badge>
                </td>
                <td>Visible to everyone, including unauthenticated visitors</td>
              </tr>
              <tr>
                <td>
                  <Badge variant="light" color="blue">
                    OWNER
                  </Badge>
                </td>
                <td>
                  Visible only to the object&apos;s owner (e.g. team manager,
                  profile owner) — never to permission holders unless they are
                  also the owner
                </td>
              </tr>
              <tr>
                <td>
                  <Badge variant="light" color="orange">
                    WITH_PERMISSION
                  </Badge>
                </td>
                <td>
                  Visible only to callers holding the field&apos;s view
                  permission — the owner does not see it unless they also hold
                  it
                </td>
              </tr>
              <tr>
                <td>
                  <Badge variant="light" color="grape">
                    OWNER_OR_PERMISSION
                  </Badge>
                </td>
                <td>
                  Visible to the object&apos;s owner, or to callers holding the
                  field&apos;s view permission
                </td>
              </tr>
            </tbody>
          </Table>
          <Alert icon={<IconInfoCircle />} color="orange" variant="light" mb="lg">
            Unauthenticated (anonymous) callers are never treated as an owner.
            They only ever see <Code>PUBLIC</Code> fields, plus any field whose
            view permission is explicitly granted to the organization&apos;s
            Anonymous or Public group — there is no ownership bypass for
            anonymous requests.
          </Alert>

          <Title order={4} mb="xs">
            Custom Field Editability
          </Title>
          <Text mb="sm">Controls who can write/update the field value:</Text>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="lg"
          >
            <thead>
              <tr>
                <th>Editability</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Badge variant="light" color="green">
                    ALWAYS
                  </Badge>
                </td>
                <td>
                  The object owner, or a caller holding the field&apos;s edit
                  permission, can set or update the value at any time
                </td>
              </tr>
              <tr>
                <td>
                  <Badge variant="light" color="yellow">
                    ONE_TIME
                  </Badge>
                </td>
                <td>
                  The owner may set the value once while it is empty; a caller
                  holding the edit permission may change it at any time
                </td>
              </tr>
              <tr>
                <td>
                  <Badge variant="light" color="orange">
                    WITH_PERMISSION
                  </Badge>
                </td>
                <td>
                  Only a caller holding the field&apos;s edit permission can set
                  or update the value — the owner cannot unless they also hold
                  it
                </td>
              </tr>
            </tbody>
          </Table>
          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mb="lg"
          >
            Re-submitting a field&apos;s current value is always treated as a
            no-op. When you are the owner of the object, setting your own{" "}
            <Code>ALWAYS</Code> and (first-time) <Code>ONE_TIME</Code> values
            does not require the{" "}
            <Code>organization:custom_fields:values:manage</Code> permission.
          </Alert>

          <Title order={4} mb="xs">
            Metadata Visibility
          </Title>
          <Text mb="sm">
            Object Metadata supports a two-level visibility model. The{" "}
            <Code>objectMetadata</Code> and{" "}
            <Code>objectMetadataBatch</Code> queries are public endpoints —
            the <Code>organization-id</Code> header is always required, but
            access to each entry is determined purely by the caller&apos;s
            permission set, not by whether the caller is authenticated:
          </Text>
          <Table striped highlightOnHover className={classes.statusTable} mb="md">
            <thead>
              <tr>
                <th>Visibility</th>
                <th>Who can read</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Badge variant="light" color="green">
                    PUBLIC
                  </Badge>
                </td>
                <td>
                  Any caller, regardless of authentication state. Returned by{" "}
                  <Code>objectMetadata</Code> and{" "}
                  <Code>objectMetadataBatch</Code> to everyone. The{" "}
                  <Code>organization-id</Code> header is still required.
                </td>
              </tr>
              <tr>
                <td>
                  <Badge variant="light" color="orange">
                    PRIVATE
                  </Badge>
                </td>
                <td>
                  Returned to any caller whose permission set includes{" "}
                  <Code>organization:metadata:view</Code> or{" "}
                  <Code>organization:metadata:manage</Code>. Authentication
                  state is irrelevant — an organization can grant{" "}
                  <Code>organization:metadata:view</Code> to its Anonymous
                  group, in which case unauthenticated callers also receive{" "}
                  <Code>PRIVATE</Code> entries. Callers whose permission set
                  does not include either permission see only{" "}
                  <Code>PUBLIC</Code> entries — no error is thrown.
                </td>
              </tr>
            </tbody>
          </Table>
          <Alert icon={<IconInfoCircle />} color="indigo" variant="light" mb="sm">
            <strong>No MISSING_PERMISSIONS error on read.</strong> Calling{" "}
            <Code>objectMetadata</Code> or <Code>objectMetadataBatch</Code>{" "}
            without the required permission silently returns only the{" "}
            <Code>PUBLIC</Code> subset rather than throwing an error. This
            makes it safe to call these queries from player-facing pages
            regardless of authentication state. The{" "}
            <Code>organization-id</Code> header is always required — callers
            without it are rejected by the platform.
          </Alert>
        </div>

        {/* ===================== GRAPHQL API REFERENCE ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconCode size={18} />}
            title="GraphQL API Reference"
            id="graphql"
          />

          <Accordion variant="separated" radius="md" mb="md">
            <Accordion.Item value="definitions-api">
              <Accordion.Control>
                Custom Field Definitions API
              </Accordion.Control>
              <Accordion.Panel>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      List Definitions
                    </Text>
                    <GqlBlock>
                      {`query {
  customFieldDefinitions(objectType: Tournament) {
    id
    objectType
    key
    name
    description
    type
    required
    unique
    order
    visibility
    editability
    options { label, value }
    defaultValue
    validationRegex
    createdAt
    updatedAt
  }
}`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      Create Definition
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: CreateCustomFieldDefinitionInput!) {
  createCustomFieldDefinition(input: $input) {
    id
    objectType
    key
    name
    type
  }
}`}
                    </GqlBlock>
                    <Text fw={600} size="sm" mb="xs" mt="md">
                      Update Definition
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!, $input: UpdateCustomFieldDefinitionInput!) {
  updateCustomFieldDefinition(id: $id, input: $input) {
    id
    key
    name
    type
    required
    unique
    visibility
    editability
  }
}`}
                    </GqlBlock>
                    <Text fw={600} size="sm" mb="xs" mt="md">
                      Delete Definition
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!) {
  deleteCustomFieldDefinition(id: $id)
}`}
                    </GqlBlock>
                  </div>
                </SimpleGrid>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="values-api">
              <Accordion.Control>Custom Field Values API</Accordion.Control>
              <Accordion.Panel>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      Query Values
                    </Text>
                    <GqlBlock>
                      {`query ($objectId: ID!) {
  customFieldValues(
    objectType: Tournament
    objectId: $objectId
  ) {
    definition {
      id, key, name, type,
      required, unique,
      visibility, editability,
      options { label, value },
      defaultValue
    }
    value
  }
}`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      Set Values
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: SetCustomFieldValuesInput!) {
  setCustomFieldValues(input: $input) {
    definition { id, key, name, type }
    value
  }
}

# Input:
# {
#   objectType: ObjectType!  # e.g. Tournament, Account, Event
#   objectId: ID!
#   fields: [{ key: String!, value: String! }]
#   deleteKeys: [String!]   # optional — delete stored values atomically
# }`}
                    </GqlBlock>
                  </div>
                </SimpleGrid>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="metadata-api">
              <Accordion.Control>Object Metadata API</Accordion.Control>
              <Accordion.Panel>
                <Alert
                  icon={<IconInfoCircle />}
                  color="indigo"
                  variant="light"
                  mb="md"
                >
                  <Code>objectMetadata</Code> and{" "}
                  <Code>objectMetadataBatch</Code> are public endpoints. The{" "}
                  <Code>organization-id</Code> header is always required.
                  Access to each entry is permission-driven: callers whose
                  permission set includes{" "}
                  <Code>organization:metadata:view</Code> or{" "}
                  <Code>organization:metadata:manage</Code> receive all
                  entries (including <Code>PRIVATE</Code>); all other callers
                  receive only <Code>PUBLIC</Code> entries. The permission
                  can be granted to the Anonymous group, so unauthenticated
                  callers can also receive <Code>PRIVATE</Code> entries if
                  the organization has configured that grant.
                </Alert>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      Query Metadata (single object)
                    </Text>
                    <GqlBlock>
                      {`query ($objectId: ID!) {
  objectMetadata(
    objectType: Tournament
    objectId: $objectId
  ) {
    key
    value
    type
    visibility
    objectType
    objectId
    createdAt
    updatedAt
  }
}`}
                    </GqlBlock>
                    <Text fw={600} size="sm" mb="xs" mt="md">
                      Query Metadata (batch, up to 100 objects)
                    </Text>
                    <GqlBlock>
                      {`query ($objectIds: [ID!]!) {
  objectMetadataBatch(
    objectType: Tournament
    objectIds: $objectIds
  ) {
    objectId
    entries {
      key, value, type, visibility
    }
  }
}`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      Set Metadata
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: SetObjectMetadataInput!) {
  setObjectMetadata(input: $input) {
    key, value, type, visibility,
    createdAt, updatedAt
  }
}

# Input:
# {
#   objectType: ObjectType!  # e.g. Tournament, Account, Event
#   objectId: ID!
#   entries: [{
#     key: String!
#     value: String!
#     type: MetadataType!
#     visibility: MetadataVisibility!  # PUBLIC or PRIVATE
#   }]
# }`}
                    </GqlBlock>
                    <Text fw={600} size="sm" mb="xs" mt="md">
                      Remove Metadata
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: RemoveObjectMetadataInput!) {
  removeObjectMetadata(input: $input)
}

# Input:
# {
#   objectType: ObjectType!  # e.g. Tournament, Account, Event
#   objectId: ID!
#   keys: [String!]!
# }`}
                    </GqlBlock>
                  </div>
                </SimpleGrid>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="utility-api">
              <Accordion.Control>Utility Queries</Accordion.Control>
              <Accordion.Panel>
                <Text fw={600} size="sm" mb="xs">
                  List Allowed Object Types
                </Text>
                <Text size="sm" c="dimmed" mb="xs">
                  Returns the list of object types that support Custom Fields
                  and Metadata in your organization.
                </Text>
                <GqlBlock>
                  {`query {
  allowedObjectTypes
}

# Returns: ["Tournament", "Player", "Event", "Leaderboard", ...]`}
                </GqlBlock>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== DATA MODEL ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconKey size={18} />}
            title="Data Model"
            id="data-model"
          />
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`CustomFieldDefinition
├── id: ID!
├── objectType: String!               # Entity type (e.g. "Tournament")
├── key: String!                       # Unique key within object type
├── name: String!                      # Display name
├── description: String                # Optional help text
├── type: CustomFieldType!             # STRING, NUMBER, BOOLEAN, DATE, EMAIL, PHONE, URL, COUNTRY, SELECT, MULTI_SELECT
├── required: Boolean!                 # Whether a value must be provided
├── unique: Boolean!                   # Whether values must be unique across objects
├── order: Int!                        # Display order (for drag-and-drop sorting)
├── visibility: PropertyVisibility!    # PUBLIC, OWNER, WITH_PERMISSION, OWNER_OR_PERMISSION
├── editability: PropertyEditability!  # ALWAYS, ONE_TIME, WITH_PERMISSION
├── options: [CustomFieldOption]       # For SELECT/MULTI_SELECT
│   ├── label: String!
│   └── value: String!
├── defaultValue: String               # Default value when none is set
├── validationRegex: String            # Optional regex for value validation
├── createdAt: DateTime!
└── updatedAt: DateTime!

CustomFieldWithValue
├── definition: CustomFieldDefinition! # The field definition
└── value: String                      # Current value (null if unset)

ObjectMetadata
├── key: String!                       # Unique key per object
├── value: String!                     # Stored value
├── type: MetadataType!                # STRING, BOOLEAN, IMAGE_URL, URL
├── visibility: MetadataVisibility!    # PUBLIC, PRIVATE
├── objectType: String!                # Entity type
├── objectId: ID!                      # Entity ID
├── createdAt: DateTime!
└── updatedAt: DateTime!`}
            </GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            Enums Reference
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} size="sm" mb="xs">
                CustomFieldType
              </Text>
              <GqlBlock>
                {`STRING
NUMBER
BOOLEAN
DATE
EMAIL
PHONE
URL
COUNTRY
SELECT
MULTI_SELECT`}
              </GqlBlock>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} size="sm" mb="xs">
                PropertyVisibility
              </Text>
              <GqlBlock>
                {`PUBLIC
OWNER
WITH_PERMISSION
OWNER_OR_PERMISSION`}
              </GqlBlock>
              <Text fw={600} size="sm" mb="xs" mt="md">
                PropertyEditability
              </Text>
              <GqlBlock>
                {`ALWAYS
ONE_TIME
WITH_PERMISSION`}
              </GqlBlock>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} size="sm" mb="xs">
                MetadataType
              </Text>
              <GqlBlock>
                {`STRING
BOOLEAN
IMAGE_URL
URL`}
              </GqlBlock>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} size="sm" mb="xs">
                MetadataVisibility
              </Text>
              <GqlBlock>
                {`PUBLIC
PRIVATE`}
              </GqlBlock>
            </Card>
          </SimpleGrid>

          <Alert icon={<IconLock />} color="cyan" variant="light" mt="md">
            Custom Fields and Metadata are scoped to your organization. Field
            definitions are shared across all objects of a given type within the
            organization, while values and metadata entries are specific to
            individual objects.
          </Alert>
        </div>
      </Stack>
    </div>
  );
};
